import AnchoredPopover from "~/components/shared/Popover/anchored";
import Emojify from "~/components/shared/Emojify";
import FriendlyTime from "~/components/shared/FriendlyTime";
import { capitalize } from "lodash";
import Icon from "~/components/shared/Icon";
import Bar, {
  ActivityProps,
  StyledAnchoredPopover,
} from "~/components/analytics/shared/Activity/Bar";

type StackedBarsProps = {
  activities: Array<ActivityProps>;
  height: number;
};

const MIN_VISIBLE_HEIGHT_RATIO = 0.03;

function segmentHeights(activities: Array<ActivityProps>) {
  const totalDuration = activities.reduce((sum, activity) => sum + activity.duration, 0);

  if (totalDuration <= 0) {
    return activities.map(() => 1 / activities.length);
  }

  const rawHeights = activities.map((activity) => {
    const height = activity.duration / totalDuration;
    return Math.max(MIN_VISIBLE_HEIGHT_RATIO, height);
  });
  const rawTotal = rawHeights.reduce((sum, height) => sum + height, 0);

  if (rawTotal > 1) {
    return rawHeights.map((height) => height / rawTotal);
  }

  return rawHeights;
}

const StackedBars = ({ activities, height }: StackedBarsProps) => {
  const heights = segmentHeights(activities);

  const bars = (
    <>
      {activities.map((activity, index) => (
        <Bar key={activity.id} activity={activity} height={heights[index]} />
      ))}
    </>
  );

  return (
    <div
      data-testid="stacked-bar-container"
      className="flex flex-col items-start"
      style={{ height: `${Math.round(height * 100.0)}%`, width: 12 }}
    >
      <StyledAnchoredPopover as={AnchoredPopover} data-testid="bar" width={300}>
        {bars}
        <div className="flex flex-col gap-1 px-2 py-1">
          <span className="block break-words font-medium">
            <Emojify text={activities[0].name} />
          </span>

          {activities[0].buildNumber && (
            <div className="dark-gray small">Build #{activities[0].buildNumber}</div>
          )}

          {activities[0].branch && (
            <div className="dark-gray small">
              <Icon icon="custom/outline/14x/branch" style={{ height: 14, width: 14 }} />{" "}
              {activities[0].branch}
            </div>
          )}

          <span className="small dark-gray block">
            <strong>Execution summary</strong>
            <br />
            {activities.map((activity) => (
              <span className="block" key={activity.id}>
                {capitalize(activity.result)}{" "}
                <FriendlyTime value={activities[0].createdAt} clickable={false} /> -{" "}
                {activity.humanDuration}
              </span>
            ))}
          </span>
        </div>
      </StyledAnchoredPopover>
    </div>
  );
};

export default StackedBars;
